import React, { useState, useMemo } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Heading, Text, Alert } from '@weave/design-system';

import {
  ChecklistMetricStatus,
  ChecklistBannerVariant,
  ChecklistActiveTab,
  MetricItemData,
  ExceptionChangeParams,
} from './handoff-checklist-v2.types';
import { ChecklistStatusBanner } from './components/status-banner/checklist-status-banner';
import { MetricsTabs } from './components/metrics-tabs/metrics-tabs';
import { MetricDetailModal } from './components/metric-detail-modal/metric-detail-modal';
import { NotesSection } from './components/notes-section/notes-section';
import { ChecklistFooter } from './components/checklist-footer/checklist-footer';
import { MetricListItem } from './components/metric-list-item/metric-list-item';

interface HandoffChecklistV2Props {
  metrics: MetricItemData[];
  title?: string;
  notes: string;
  successMessage?: string;
  onNotesChange?: (value: string) => void;
  onCancel?: () => void;
  onSaveAndClose?: () => void;
  onSubmit?: () => void;
  onRefresh?: () => void;
  isSaving?: boolean;
  isSubmitting?: boolean;
  isLoading?: boolean;
  error?: string | null;
  readOnly?: boolean;
  showWhereToFix?: boolean;
  onExceptionChange?: (
    metricId: string,
    criteriaIndex: number,
    exception: string
  ) => void;
  onLocationExceptionChange?: (params: ExceptionChangeParams) => void;
}

export const HandoffChecklistV2 = ({
  metrics,
  notes,
  title = 'Handoff Checklist',
  successMessage,
  onNotesChange = () => {},
  onCancel = () => {},
  onSaveAndClose = () => {},
  onSubmit = () => {},
  onRefresh = () => {},
  isSaving = false,
  isSubmitting = false,
  readOnly = false,
  showWhereToFix = true,
  onExceptionChange,
  onLocationExceptionChange,
  isLoading = false,
  error = null,
}: HandoffChecklistV2Props) => {
  const [activeTab, setActiveTab] = useState<ChecklistActiveTab>('all');
  const [selectedCompletedMetric, setSelectedCompletedMetric] =
    useState<MetricItemData | null>(null);

  // Compute metrics statistics
  const stats = useMemo(() => {
    const required = metrics.filter((m) => m.isRequired);
    const optional = metrics.filter((m) => !m.isRequired);

    const blockedRequired = required.filter(
      (m) => m.status === ChecklistMetricStatus.BLOCKED && !m.isLoading
    );
    const exceptionNotedRequired = required.filter(
      (m) => m.status === ChecklistMetricStatus.EXCEPTION_NOTED && !m.isLoading
    );
    const incompleteOptional = optional.filter(
      (m) =>
        m.status !== ChecklistMetricStatus.PASSED &&
        m.status !== ChecklistMetricStatus.EXCEPTION_NOTED &&
        !m.isLoading
    );

    const hasBlockers = blockedRequired.length > 0;
    const hasExceptionsNoted = exceptionNotedRequired.length > 0;
    const hasWarnings = incompleteOptional.length > 0;
    const isAnyLoading = isLoading || metrics.some((m) => m.isLoading);
    const isChecklistEmpty = !readOnly && metrics.length === 0;

    let bannerVariant = ChecklistBannerVariant.SUCCESS;
    if (hasBlockers) {
      bannerVariant = ChecklistBannerVariant.BLOCKED;
    } else if (hasExceptionsNoted) {
      bannerVariant = ChecklistBannerVariant.EXCEPTION_NOTED;
    } else if (hasWarnings) {
      bannerVariant = ChecklistBannerVariant.WARNING;
    }

    return {
      required,
      optional,
      blockedRequired,
      exceptionNotedRequired,
      incompleteOptional,
      hasBlockers: hasBlockers || isAnyLoading || isChecklistEmpty || Boolean(error), // Disable submit if anything is still loading, metrics empty, or error
      bannerVariant,
      isAnyLoading: isAnyLoading || isChecklistEmpty,
    };
  }, [metrics, isLoading, error, readOnly]);

  // Filter metrics based on active tab
  const displayedMetrics = useMemo(() => {
    if (activeTab === 'blocked') {
      return metrics.filter((m) => m.status === ChecklistMetricStatus.BLOCKED);
    }
    return metrics;
  }, [metrics, activeTab]);

  const handleTakeNotes = () => {
    // TextareaField does not expose a direct textarea ref; target a stable container and focus its textarea.
    const notesTextarea = document.querySelector(
      '#handoff-checklist-notes-input textarea'
    ) as HTMLTextAreaElement | null;

    if (notesTextarea) {
      notesTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      notesTextarea.focus();
    }
  };

  const handleReviewOptional = () => {
    if (stats.incompleteOptional.length > 0) {
      // Find the first incomplete optional metric item and scroll to it
      const firstIncompleteId = stats.incompleteOptional[0].id;

      // Scroll to the metric item
      const element = document.getElementById(`metric-item-${firstIncompleteId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Group displayed metrics into Required and Optional
  const displayedRequired = displayedMetrics.filter((m) => m.isRequired);
  const displayedOptional = displayedMetrics.filter((m) => !m.isRequired);

  return (
    <div css={styles.wrapper}>
      <Heading level={2}>{title}</Heading>
      {!readOnly && error && <Alert type="error">{error}</Alert>}
      {!readOnly && !stats.isAnyLoading && !error && (
        <ChecklistStatusBanner
          variant={stats.bannerVariant}
          successMessage={successMessage}
          blockedCount={stats.blockedRequired.length}
          blockedMetricNames={stats.blockedRequired.map((m) => m.name)}
          exceptionNotedCount={stats.exceptionNotedRequired.length}
          exceptionNotedMetricNames={stats.exceptionNotedRequired.map((m) => m.name)}
          onRefresh={onRefresh}
          onTakeNotes={handleTakeNotes}
          onReviewOptional={handleReviewOptional}
        />
      )}

      <MetricsTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        blockedCount={stats.blockedRequired.length}
      />

      {displayedRequired.length > 0 && (
        <section css={styles.section}>
          <Text as="div" weight="bold">
            Required
          </Text>
          {displayedRequired.map((metric) => (
            <div id={`metric-item-${metric.id}`} key={metric.id}>
              <MetricListItem
                metric={metric}
                onOpenMetricDetail={() => setSelectedCompletedMetric(metric)}
                defaultOpen={readOnly}
                forceAccordion={readOnly}
                readOnly={readOnly}
                showWhereToFix={showWhereToFix}
                onExceptionChange={onExceptionChange}
                onLocationExceptionChange={onLocationExceptionChange}
              />
            </div>
          ))}
        </section>
      )}

      {displayedOptional.length > 0 && (
        <section css={styles.section}>
          <Text as="div" weight="bold">
            Optional
          </Text>
          {displayedOptional.map((metric) => (
            <div id={`metric-item-${metric.id}`} key={metric.id}>
              <MetricListItem
                metric={metric}
                onOpenMetricDetail={() => setSelectedCompletedMetric(metric)}
                defaultOpen={readOnly}
                forceAccordion={readOnly}
                readOnly={readOnly}
                showWhereToFix={showWhereToFix}
                onExceptionChange={onExceptionChange}
                onLocationExceptionChange={onLocationExceptionChange}
              />
            </div>
          ))}
        </section>
      )}

      <div css={styles.divider} />
      <NotesSection notes={notes} onChange={onNotesChange} readOnly={readOnly} />

      {!readOnly && (
        <ChecklistFooter
          onCancel={onCancel}
          onSaveAndClose={onSaveAndClose}
          onSubmit={onSubmit}
          isSubmitDisabled={stats.hasBlockers}
          isSaving={isSaving}
          isSubmitting={isSubmitting}
          isLoading={stats.isAnyLoading}
          allowExceptions={metrics.some((m) => m.isRequired && m.allowExceptions)}
          error={error}
        />
      )}

      <MetricDetailModal
        metric={selectedCompletedMetric}
        isOpen={selectedCompletedMetric !== null}
        onClose={() => setSelectedCompletedMetric(null)}
      />
    </div>
  );
};

const styles = {
  wrapper: css`
    padding: ${theme.spacing(1, 0)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  divider: css`
    border-top: 1px solid #d9d8d8;
    margin: ${theme.spacing(2, 0)};
  `,
};
